/** Шторка: ручка, «шапка» с итогами и прокручиваемое содержимое. */

import {
  useRef,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';

import type { BottomSheetApi } from '../hooks/useBottomSheet';

const SNAP_TITLE: Record<string, string> = {
  collapsed: 'Свёрнуто. Потяните вверх или нажмите, чтобы раскрыть',
  mid: 'Наполовину раскрыто. Нажмите, чтобы раскрыть полностью',
  full: 'Раскрыто. Нажмите, чтобы свернуть',
};

interface BottomSheetProps {
  sheet: BottomSheetApi;
  label: string;
  /** Видно всегда, даже в свёрнутом виде. */
  peek: ReactNode;
  children: ReactNode;
}

export function BottomSheet({
  sheet,
  label,
  peek,
  children,
}: BottomSheetProps): JSX.Element {
  const startY = useRef(0);
  const moved = useRef(false);

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>): void => {
    startY.current = event.clientY;
    moved.current = false;
    sheet.onDragStart(event);
  };

  const onPointerMove = (event: ReactPointerEvent<HTMLElement>): void => {
    if (Math.abs(event.clientY - startY.current) > 8) moved.current = true;
  };

  // Тап по ручке без протяжки — переключение положений.
  const onClick = (): void => {
    if (!moved.current) sheet.cycle();
  };

  return (
    <section
      className={`sheet${sheet.dragging ? ' is-dragging' : ''} sheet--${sheet.snap}`}
      ref={sheet.sheetRef}
      style={{ '--sheet-top': `${sheet.fullTop}px` } as CSSProperties}
      aria-label={label}
    >
      <div className="sheet__head" ref={sheet.peekRef}>
        <button
          type="button"
          className="sheet__grab"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onClick={onClick}
          aria-label={SNAP_TITLE[sheet.snap] ?? 'Шторка'}
          aria-expanded={sheet.snap === 'full'}
        >
          <span className="sheet__handle" aria-hidden="true" />
        </button>
        <div
          className="sheet__peek"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
        >
          {peek}
        </div>
      </div>

      <div className="sheet__scroll">{children}</div>
    </section>
  );
}
